import { ROOT, SINGLE_DEALER_ROOT } from './data-service';
import gtmEvent from './gtmEvents';

export function sortDealersNADFirst(a, b) {
  const isNadA = !!a?.company_types?.find(ct => ct.type === 'nokian' || ct.type === 'nadna');
  const isNadB = !!b?.company_types?.find(ct => ct.type === 'nokian' || ct.type === 'nadna');
  if (isNadA && !isNadB) {
    return -1;
  }
  if (!isNadA && isNadB) {
    return 1;
  }
  return 0;
}

/**
 * Sorts dealers to have closest [amount] of NAD dealers first
 * Rest of the NAD dealers and non-NAD dealers follow after that sorted by distance.
 *
 * @param {array<Dealer>} dealers
 * @param {object} options
 * @param {number} options.amount - Number of NAD dealers to show first
 * @param {number} options.radius - Distance in kilometers to consider NAD dealers as "close"
 *
 * @returns {array<Dealer>}
 */
export function sortDealersClosestNADFirst(dealers, options = {}) {
  const defaults = {
    amount: 3,
    radius: 50,
  };
  const { amount, radius } = { ...defaults, ...options };

  const closestNadDealers = dealers
    .filter(d => d?.company_types?.find(ct => ct.type === 'nokian' || ct.type === 'nadna'))
    .sort((a, b) => a.distance - b.distance)
    .filter(d => d.distance <= radius)
    .slice(0, amount)
    .map(d => ({ ...d, featured: true})); // Add featured flag, so that UI can highlight these dealers

  // Find the rest (not NAD or not in the first [amount] of NADs)
  const nadDealerIds = new Set(closestNadDealers.map(d => d.uuid));
  const restDealers = dealers
    .filter(d => !nadDealerIds.has(d.uuid))
    .sort((a, b) => a.distance - b.distance);

  return [...closestNadDealers, ...restDealers];
}

export function findClosestDealers(dealers, distance = 100, amount = 10) {
  const allDealers = [...dealers];
  allDealers.sort((a, b) => {
    return a.distance < b.distance ? -1 : 1;
  });

  // Include closest dealers which are located within specified kilometers from users location
  const dealersWithinDistance = allDealers.filter(dealer => dealer.distance <= distance);
  if (dealersWithinDistance.length > 0) {
    return dealersWithinDistance.slice(0, amount);
  }

  const closestDealers = allDealers.slice(0, amount);
  return closestDealers;
}

/**
 * Calculate distance wetbeen two latlons.
 *
 * @param {number} lat1 - Point 1 latitude
 * @param {number} lon1 - Point 1 longitude
 * @param {number} lat2 - Point 2 latitude
 * @param {number} lon2 - Point 2 longitude
 * @return {number} distance in km
 */
export function latlonDistance(lat1, lon1, lat2, lon2) {
  const lat = ((lat1 - lat2) * Math.PI) / 180;
  const lon = ((lon1 - lon2) * Math.PI) / 180;

  const a =
    Math.sin(lat / 2) * Math.sin(lat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(lon / 2) *
      Math.sin(lon / 2);

  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Checks whether difference between coordinates is large enough to warrant new query to server.
 *
 * @param {google.maps.LatLngBoundsLiteral} newLocation New location containing coordinate boundaries.
 * @param {google.maps.LatLngBoundsLiteral} lastLocation Old location containing coordinate boundaries.
 * @param {number} distance Checks if location boundaries are withing specifed amount of kilometers.
 * @return {boolean}
 */
export function isWithinDistance(newLocation, lastLocation, distance) {
  if (!lastLocation || !newLocation || distance <= 0) {
    return true;
  }

  const nWdistance = latlonDistance(newLocation.north, newLocation.west, lastLocation.north, lastLocation.west);
  if (nWdistance > distance) {
    return false;
  }

  const seDistance = latlonDistance(newLocation.south, newLocation.east, lastLocation.south, lastLocation.east);
  if (seDistance > distance) {
    return false;
  }

  return true;
}

/**
 * Enforce https-protocol to url
 * @param {string} url - Url
 * @return {string} Url with https protocol
 */
export function enforceProtocol(url) {
  if (url === null) {
    return '';
  }

  if (url.indexOf('https://') === -1) {
    return `https://${url}`;
  }

  return url;
}

/**
 * Formats numeric distance to string.
 *
 * @param {Number} distance Distance in kilometers.
 * @param {String} [unit='km'] Output unit: km or miles.
 * @param {String} [separator='.'] Decimal separator.
 * @return {String} Formatted distance.
 */
export function formatDistance(distance, unit, separator) {
  unit = unit || 'km';
  separator = separator || '.';
  if (!distance) {
    return '';
  }

  if (unit === 'miles') {
    distance = distance / 1.609347;
  }

  distance = distance.toFixed(1).replace('.0', '');

  if (distance > 100) {
    distance = distance.split('.')[0];
  }

  return distance.replace('.', separator);
}

export const findPacContainer = (autocomplete) => {
  if (!autocomplete?.gm_accessors_?.place) {
    return null;
  }

  for (let key in autocomplete.gm_accessors_.place) {
    const placeField = autocomplete.gm_accessors_.place[key];
    if (typeof placeField === 'object' && placeField.gm_accessors_ && placeField.gm_accessors_.input) {
      for (let inputKey in placeField.gm_accessors_.input) {
        const inputField = placeField.gm_accessors_.input[inputKey];
        if (typeof inputField === 'object') {
          for (let prop in inputField) {
            const div = inputField[prop];
            if (typeof div === 'object' && div.classList && div.classList.contains('pac-container')) {
              return div;
            }
          }
        }
      }
    }
  }

  return null;
};

/**
 * Updates browser history url if current url differs from provided options.
 *
 * @param {object} options History options.
 * @param {string} [options.slug] Single dealer slug.
 * @param {string} [options.dealerName] Single dealer name for analytics.
 * @param {boolean} [options.skipLandingPage] Whether landing page is skipped
 * @param {string[]} [options.filters] Array of filter ids.
 * @param {string} [options.address] Address that was searched.
 */
export function updateUrl(options = {}) {
  let url = ROOT;
  if (options.slug) {
    url = SINGLE_DEALER_ROOT + options.slug + '/';
  }

  // Some query parameters should be preserved
  const newQueryParams = new URLSearchParams();
  const queryParams = new URLSearchParams(window.location.search);
  const validParams = ['gclid', 'location', 'cid', 'altdev', '_gl', 'map_type'];
  validParams.forEach(param => {
    if (queryParams.has(param)) {
      newQueryParams.append(param, queryParams.get(param));
    }
  });

  if (!options.slug) {
    if (options.skipLandingPage && !options.address) {
      newQueryParams.append('skip_landing', 'true');
    }
    if (options.filters?.length > 0) {
      newQueryParams.append('filters', options.filters.join(','));
    }
    if (options.address) {
      newQueryParams.append('address', options.address);
    }
  }

  const query = newQueryParams.size > 0 ? '?' + newQueryParams.toString() : '';
  if (window.location.search === query && url === window.location.pathname) {
    return;
  }
  //console.log('Url updated: ' + url + query);
  window.history.replaceState({}, null, url + query);

  if (options.dealerName) {
    gtmEvent({
      event: 'VirtualPageview',
      virtualPageURL: url,
      dealer_name: options.dealerName,
    });
  }
}

export function getStoredLocation() {
  try {
    const location = sessionStorage.getItem('dealerLocation');
    if (location) {
      const parsedLocation = JSON.parse(location);
      if (!validateLocation(parsedLocation)) return null;
      return parsedLocation;
    }
  } catch (e) {}

  return null;
}

export function storeLocation(location) {
  if (!validateLocation(location)) return;
  try {
    sessionStorage.setItem('dealerLocation', JSON.stringify(location));
  } catch (e) {}
}

function validateLocation(location) {
  if (!location) {
    return false;
  }

  if (!location.lat || !location.lng || isNaN(location.lat) || isNaN(location.lng)) {
    return false;
  }

  return true;
}
